/**
 * MoreMenuSheet - Mobile bottom sheet holding the navigation that doesn't
 * fit in the BottomTabBar's four primary tabs.
 *
 * The tab bar only has room for Dashboard / Orders / Inspect / Settings, so
 * everything else the Sidebar shows on desktop — Buildings, Calendar, Map,
 * the project + recurring lists, and the role-gated Administration group
 * (Admin, CMS, API Keys) — was simply unreachable on a phone. This sheet is
 * where those links live: it renders the same items from the same hook the
 * Sidebar uses (useNavItems), so the two surfaces can't drift apart and an
 * admin sees the same set of tools on either device.
 *
 * Layout:
 *	 - A translucent backdrop covering the page (tap to dismiss)
 *	 - A white panel that slides up and rests directly on top of the
 *	   BottomTabBar, so the "More" tab stays visible and tappable to close
 *
 * Visibility:
 *	 - Mobile only (`tablet:hidden`) — on tablet+ the Sidebar shows all of this
 *	 - Renders nothing at all when `isOpen` is false
 *
 * Accessibility:
 *	 - role="dialog" + aria-modal so screen readers treat it as a layer
 *	 - Escape key closes the sheet
 *	 - Every row clears the 44px minimum touch target
 *
 * @example
 * <MoreMenuSheet isOpen={moreOpen} onClose={() => setMoreOpen(false)} excludeHrefs={['/', '/work-orders']} />
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavItems, isNavItemActive, type NavItem } from '../../../hooks/useNavItems';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { BRAND_TAGLINE, PoweredByDirt } from '../../atoms';

/**
 * Props for the MoreMenuSheet component.
 *
 * @property isOpen		  - Controls whether the sheet is rendered
 * @property onClose	  - Fired on backdrop tap, Escape, or after navigating
 * @property excludeHrefs - Routes already reachable from the tab bar, so the
 *							sheet doesn't repeat them. Passed in by the caller
 *							(BottomTabBar) so the tab list stays the one source
 *							of truth for what's already on screen.
 * @property className	  - Optional outer className merged onto the root element
 */
export interface MoreMenuSheetProps {
	isOpen: boolean;
	onClose: () => void;
	excludeHrefs?: string[];
	className?: string;
}

const MoreMenuSheet: React.FC<MoreMenuSheetProps> = ({ isOpen, onClose, excludeHrefs = [], className }) => {
	/* ---------------------------------------------------------------
	 * Hooks
	 * --------------------------------------------------------------- */

	/** Current route location for active-state detection */
	const location = useLocation();

	/** Programmatic navigation for client-side routing */
	const navigate = useNavigate();

	/** Shared, role-gated nav definitions — identical to the Sidebar's */
	const { mainNavItems, adminNavItems } = useNavItems();

	/**
	 * Drives the slide-up transition. The panel mounts translated off the
	 * bottom edge and flips to its resting position on the next frame, which
	 * is what gives the browser two states to animate between. Purely visual —
	 * the sheet is fully usable if the frame callback never fires.
	 */
	const [entered, setEntered] = useState(false);

	/* Reset + kick off the enter transition each time the sheet opens */
	useEffect(() => {
		if (!isOpen) {
			setEntered(false);
			return;
		}
		const frame = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(frame);
	}, [isOpen]);

	/* Escape closes the sheet, matching the Modal organism's behavior */
	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	/* Freeze the page behind the sheet so a swipe on the backdrop
	   doesn't scroll the screen underneath */
	useBodyScrollLock(isOpen);

	/* ---------------------------------------------------------------
	 * Derived data — hide anything the tab bar already links to
	 * --------------------------------------------------------------- */

	/** Main-section links that aren't already a bottom tab */
	const sheetNavItems = mainNavItems.filter((item) => !excludeHrefs.includes(item.href));

	/* Nothing to show and nothing open — skip rendering entirely */
	if (!isOpen) return null;

	/* ---------------------------------------------------------------
	 * Handlers
	 * --------------------------------------------------------------- */

	/**
	 * Navigates to the tapped route and dismisses the sheet, so the user
	 * lands on the page rather than on the page behind a still-open overlay.
	 */
	const handleItemClick = (href: string) => {
		navigate(href);
		onClose();
	};

	/* ---------------------------------------------------------------
	 * Render helper — one navigation row (icon + label)
	 * --------------------------------------------------------------- */

	/**
	 * Renders a single row. The active row gets brand-primary text and a
	 * subtle tint, mirroring the Sidebar's active treatment.
	 */
	const renderItem = (item: NavItem) => {
		const active = isNavItemActive(item.href, location.pathname);

		return (
			<button
				key={item.href}
				type="button"
				onClick={() => handleItemClick(item.href)}
				className={[
					/* BEM element + active modifier */
					'more-menu-sheet__item',
					active ? 'more-menu-sheet__item--active' : '',
					/* Full-width row: icon then label, 44px minimum touch target */
					'flex items-center gap-12px w-full min-h-44px px-20px py-10px',
					'text-left text-14px font-sans',
					/* Reset default button chrome */
					'bg-transparent border-none',
					'transition-colors duration-200',
					active
						? 'text-brand-primary font-semibold bg-brand-primary-lighter/10'
						: 'text-brand-text-muted',
					/* Focus ring for keyboard navigation */
					'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset',
				].join(' ')}
				aria-current={active ? 'page' : undefined}
			>
				{/* Icon — inherits text color from the row for active/inactive styling */}
				<span className="flex-shrink-0">{item.icon}</span>
				<span>{item.label}</span>
			</button>
		);
	};

	/* ---------------------------------------------------------------
	 * Component JSX
	 * --------------------------------------------------------------- */

	return (
		<div
			className={[
				/* BEM block marker */
				'more-menu-sheet',
				/* Mobile only — the Sidebar covers this ground on tablet+ */
				'tablet:hidden',
				/* Full-viewport layer. z-40 sits UNDER the BottomTabBar's z-50 on
				   purpose: the bar stays visible and tappable so the "More" tab
				   itself toggles the sheet closed. */
				'fixed inset-0 z-40',
				className ?? '',
			].join(' ')}
			data-ref="organism-more-menu-sheet"
			role="dialog"
			aria-modal="true"
			aria-label="More navigation"
		>
			{/* ===== Backdrop — dims the page and dismisses on tap ===== */}
			<div
				className="more-menu-sheet__backdrop absolute inset-0 bg-black/40"
				onClick={onClose}
				aria-hidden="true"
			/>

			{/* ===== Sheet panel ===== */}
			{/* Rests directly on top of the tab bar: 64px of bar plus whatever the
				device reserves for the home indicator. */}
			<div
				className={[
					'more-menu-sheet__panel',
					'absolute left-0 right-0 bottom-[calc(64px+env(safe-area-inset-bottom,0px))]',
					'bg-white border-t border-brand-border rounded-t-2xl shadow-lg',
					/* Cap the height so a long list scrolls instead of covering the screen */
					'max-h-[70vh] overflow-y-auto',
					/* Slide-up transition — see the `entered` state above */
					'transform transition-transform duration-200 ease-out',
					entered ? 'translate-y-0' : 'translate-y-full',
				].join(' ')}
			>
				{/* Grabber bar — the standard visual affordance for a bottom sheet */}
				<div className="more-menu-sheet__grabber mx-auto mt-8px mb-4px h-4px w-40px rounded-full bg-brand-border" aria-hidden="true" />

				{/* ===== Main navigation section ===== */}
				{sheetNavItems.length > 0 && (
					<div className="more-menu-sheet__section py-8px">
						<p className="more-menu-sheet__section-label px-20px mb-4px text-10px font-sans font-semibold text-brand-text-subtle uppercase tracking-widest">
							Navigation
						</p>
						{sheetNavItems.map(renderItem)}
					</div>
				)}

				{/* ===== Administration section (role-gated) ===== */}
				{/* Only rendered for admin / super_admin / developer users — this is
					the group that had no mobile entry point before. */}
				{adminNavItems.length > 0 && (
					<div className="more-menu-sheet__section border-t border-brand-border py-8px">
						<p className="more-menu-sheet__section-label px-20px mb-4px text-10px font-sans font-semibold text-brand-text-subtle uppercase tracking-widest">
							Administration
						</p>
						{adminNavItems.map(renderItem)}
					</div>
				)}

				{/* ===== Brand footer =====
				    Phones never see the sidebar (tablet+ only), so without this the
				    G Brands tagline and the "Powered by DIRT" credit (beta feedback
				    6b/6c) would be desktop-only. Same atom + constant as the
				    sidebar, so the two can't drift. Also gives the last row its
				    breathing room above the panel edge. */}
				<div className="more-menu-sheet__brand border-t border-brand-border px-20px pt-16px pb-12px flex flex-col items-center gap-12px">
					<p className="more-menu-sheet__tagline font-sans text-12px text-brand-text-muted text-center">{BRAND_TAGLINE}</p>
					<PoweredByDirt />
				</div>
			</div>
		</div>
	);
};

export default MoreMenuSheet;
